import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";

import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  FEEDBACK_CATEGORY_LABELS,
  FeedbackSentiment,
  type UserFeedbackRecord,
} from "@/shared/domain/agentThread";

import styles from "./AgentThreadFeedback.module.css";

interface PriorRatingPillProps {
  priorRating: UserFeedbackRecord;
  onEdit: () => void;
  eventContext: { threadId: string; messageSequenceNum: number };
}

/**
 * Compact indicator shown in place of the thumbs composer when the user has
 * already rated the message. Clicking it reopens the wizard to amend.
 */
export const PriorRatingPill: React.FC<PriorRatingPillProps> = ({
  priorRating,
  onEdit,
  eventContext,
}) => {
  const theme = useTheme();
  const isPositive = priorRating.sentiment === FeedbackSentiment.POSITIVE;
  // Match the composer's iconography: thumbs up/down rather than a generic
  // check, so the glyph alone reveals which way the user rated. The previous
  // CheckCircle-with-colour-only design relied on subtle red/green to signal
  // negative — easy to miss for low-vision users and ambiguous in muted
  // theme variants.
  const Icon = isPositive ? ThumbUpAltIcon : ThumbDownAltIcon;
  const iconColor = isPositive
    ? theme.palette.success.main
    : theme.palette.error.main;
  // Pair each direction with its own analytics event so dashboards can
  // separate "edited a positive rating" from "edited a negative rating" —
  // the previous code reported every edit as ``AgentThreadFeedbackPositiveClicked``.
  const eventName = isPositive
    ? "AgentThreadFeedbackPositiveClicked"
    : "AgentThreadFeedbackNegativeClicked";
  const label =
    priorRating.categories.length > 0
      ? priorRating.categories
          .map((c) => FEEDBACK_CATEGORY_LABELS[c])
          .join(" · ")
      : "Feedback recorded";

  return (
    <Box className={styles.feedbackContainer}>
      <RobotoTooltip title="Click to edit your feedback">
        <span>
          <RobotoIconButton
            eventName={eventName}
            eventProperties={eventContext}
            onClick={onEdit}
            className={styles.iconButton}
          >
            <Icon className={styles.thumbIcon} sx={{ color: iconColor }} />
          </RobotoIconButton>
        </span>
      </RobotoTooltip>
      <RobotoTypography
        variant="caption"
        className={styles.alreadyRatedLabel}
        sx={{ color: theme.palette.text.secondary }}
      >
        {label}
      </RobotoTypography>
    </Box>
  );
};
